import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAlerts, type Alert } from '../hooks/useAlerts'
import { useAuthFetch } from '../lib/useAuthFetch'
import { OperatorTypeFilter, type OperatorType } from '../components/filters/OperatorTypeFilter'
import { FeedItem, Pill, DetailPanel, PageHeader } from '../components/ui'
import type { FeedTone, PillTone } from '../components/ui'
import { AlertAssignModal } from '../components/shared/AlertAssignModal'
import { summarizeDeliveries, type NotifyOption, type DeliveryOutcome } from '../lib/taskAssign'

const API = import.meta.env.VITE_API_URL ?? ''

// One sitting's worth of triage. Deliberately not "all of them": an unbounded
// feed teaches you to stop reading it.
const ALERT_PAGE = 12

// ── Helpers (same as CommandCenter) ───────────────────────────────────────

function timeAgo(iso: string) {
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  return `${Math.floor(h / 24)}d ago`
}

type Sev = 'critical' | 'high' | 'medium' | 'info'

function getSev(alert: Alert): Sev {
  if (alert.alert_type === 'price_drop') {
    if (alert.old_value && alert.new_value) {
      const pct = (parseFloat(alert.old_value) - parseFloat(alert.new_value)) / parseFloat(alert.old_value)
      if (pct >= 0.2) return 'critical'
      if (pct >= 0.1) return 'high'
    }
    return 'high'
  }
  if (alert.alert_type === 'new_promo') return 'high'
  if (alert.alert_type === 'new_sku') return 'medium'
  return 'info'
}

function getSevLabel(sev: Sev) {
  return { critical: 'CRITICAL', high: 'HIGH', medium: 'MEDIUM', info: 'INFO' }[sev]
}

function getTitle(alert: Alert) {
  const name = alert.competitor_name
  if (alert.alert_type === 'price_drop') {
    if (alert.old_value && alert.new_value) {
      const pct = Math.round((parseFloat(alert.old_value) - parseFloat(alert.new_value)) / parseFloat(alert.old_value) * 100)
      return `${name} dropped pricing ${pct}%`
    }
    return `${name} dropped prices`
  }
  if (alert.alert_type === 'new_promo') return `${name} launched a new promotion`
  if (alert.alert_type === 'new_sku') return `${name} added new products`
  if (alert.alert_type === 'price_increase') return `${name} raised prices`
  return `${name} — ${alert.alert_type.replace(/_/g, ' ')}`
}

function getChange(alert: Alert) {
  if (alert.old_value && alert.new_value && alert.alert_type === 'price_drop')
    return `$${alert.old_value} → $${alert.new_value}`
  if (alert.alert_type === 'new_promo') return alert.new_value || 'New deal detected on their menu'
  if (alert.alert_type === 'new_sku') return alert.entity_name || alert.new_value || 'Menu update detected'
  return alert.alert_type.replace(/_/g, ' ')
}

function getSub(alert: Alert) {
  const change = getChange(alert)
  if (alert.alert_type === 'price_drop') return `${change} · Check comparable products and margin`
  if (alert.alert_type === 'new_promo') return `${change} · Compare offer and margin`
  if (alert.alert_type === 'new_sku') return `${change} · Check assortment overlap`
  return change
}

function getChips(alert: Alert): { label: string; tone: PillTone }[] {
  const chips: { label: string; tone: PillTone }[] = []
  if (alert.location_name) chips.push({ label: alert.location_name, tone: 'neutral' })
  if (alert.alert_type === 'price_drop' && alert.old_value && alert.new_value) {
    const delta = parseFloat(alert.new_value) - parseFloat(alert.old_value)
    chips.push({ label: `−$${Math.abs(delta).toFixed(0)} change`, tone: 'danger' })
  }
  if (alert.alert_type === 'new_promo') chips.push({ label: 'NEW PROMO', tone: 'warning' })
  if (alert.alert_type === 'new_sku') chips.push({ label: 'CATALOG CHANGE', tone: 'accent' })
  return chips
}

function priceChangePercent(alert: Alert) {
  if (alert.alert_type !== 'price_drop' || !alert.old_value || !alert.new_value) return 0
  const oldPrice = parseFloat(alert.old_value)
  const newPrice = parseFloat(alert.new_value)
  if (!Number.isFinite(oldPrice) || !Number.isFinite(newPrice) || oldPrice <= 0) return 0
  return Math.max(0, (oldPrice - newPrice) / oldPrice * 100)
}

// Attention order uses only fields present in the alert payload. This is not a
// revenue-impact model and intentionally makes no claim about the operator's
// own price, margin, carried status, or affected-SKU breadth.
function attentionScore(alert: Alert) {
  const typeWeight: Record<string, number> = {
    price_drop: 500,
    new_promo: 350,
    price_increase: 220,
    new_sku: 160,
    promo_ended: 100,
  }
  const confidenceWeight = alert.confidence === 'high' ? 30 : alert.confidence === 'medium' ? 15 : 0
  const ageHours = Math.max(0, (Date.now() - new Date(alert.created_at).getTime()) / 3_600_000)
  const recencyWeight = Math.max(0, 48 - Math.min(ageHours, 48))
  return (typeWeight[alert.alert_type] ?? 50) + Math.min(priceChangePercent(alert), 50) * 4 + confidenceWeight + recencyWeight
}

function decisionImpact(alert: Alert) {
  const near = alert.location_name ? ` near ${alert.location_name}` : ''
  if (alert.alert_type === 'price_drop') {
    return `A rival price cut${near} can change your competitive price position. Confirm comparable products and your margin before responding.`
  }
  if (alert.alert_type === 'new_promo') {
    return `A new promotion${near} may change the value shoppers see. Compare the offer before deciding whether to respond.`
  }
  if (alert.alert_type === 'new_sku') {
    return `A new product${near} may expose an assortment gap. Check whether it overlaps with demand you serve.`
  }
  return `This is the highest-ranked visible rival move${near}. Review the evidence before taking action.`
}

// Severity → FeedItem icon tone (block events keep their rose accent).
function sevTone(sev: Sev, type: string): FeedTone {
  if (type === 'block_cancelled' || type === 'competitor_blocked') return 'rose'
  if (sev === 'critical') return 'danger'
  if (sev === 'high') return 'warn'
  if (sev === 'medium') return 'teal'
  return 'neutral'
}

// Bucket an alert by recency for the sticky time-group headers (s12 mockup).
type TimeBucket = 'today' | 'yesterday' | 'older'
function timeBucket(iso: string): TimeBucket {
  const now = new Date()
  const then = new Date(iso)
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
  const t = then.getTime()
  if (t >= startToday) return 'today'
  if (t >= startToday - 86400000) return 'yesterday'
  return 'older'
}
const BUCKET_LABEL: Record<TimeBucket, string> = { today: 'Today', yesterday: 'Yesterday', older: 'Earlier' }

function AlertIcon({ type }: { type: string }) {
  if (type === 'price_drop' || type === 'price_increase')
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
  if (type === 'new_promo')
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/></svg>
  if (type === 'new_sku')
    return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
}

// ── Dropdown component ─────────────────────────────────────────────────────
function Dropdown({
  id, label, active, open, onToggle, options
}: {
  id: string
  label: string
  active: boolean
  open: boolean
  onToggle: () => void
  options: { value: string; label: string; dot?: string }[]
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <span style={{
        fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 600,
        letterSpacing: '0.14em', textTransform: 'uppercase' as const,
        color: 'var(--text-3)',
      }}>{id}</span>
      <div style={{ position: 'relative' }}>
        <button
          onClick={onToggle}
          style={{
            display: 'flex', alignItems: 'center', gap: 5,
            padding: '5px 10px', borderRadius: 8,
            background: active ? 'var(--accent-soft)' : 'var(--surface)',
            border: `1px solid ${active ? 'var(--accent)' : 'var(--border-2)'}`,
            color: active ? 'var(--accent)' : 'var(--text-2)',
            fontSize: 11.5, fontWeight: 600,
            cursor: 'pointer', fontFamily: 'var(--sans)', whiteSpace: 'nowrap' as const,
          }}
        >
          {label}
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="8" height="8"><polyline points="6 9 12 15 18 9"/></svg>
        </button>
        {open && (
          <div style={{
            position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 200,
            background: 'var(--surface)', border: '1px solid var(--border-2)',
            borderRadius: 10, boxShadow: 'var(--card-shadow-lg)',
            minWidth: 160, padding: 4,
          }}>
            {options.map(opt => (
              <button
                key={opt.value}
                onClick={() => { /* handled by parent */ }}
                data-value={opt.value}
                style={{
                  display: 'flex', width: '100%', alignItems: 'center', gap: 7,
                  padding: '7px 10px', borderRadius: 7, border: 'none',
                  cursor: 'pointer', textAlign: 'left' as const,
                  background: 'transparent', color: 'var(--text-1)',
                  fontSize: 12, fontFamily: 'var(--sans)',
                }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'var(--accent-soft)' }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'transparent' }}
              >
                {opt.dot && <span style={{ width: 7, height: 7, borderRadius: '50%', background: opt.dot, flexShrink: 0 }} />}
                {opt.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ── Main Component ─────────────────────────────────────────────────────────

type StatusFilter = 'unreviewed' | 'reviewed' | 'all'
type SortKey = 'impact' | 'newest' | 'severity' | 'rival' | 'location'

export default function AlertFeed() {
  const authFetch = useAuthFetch()
  const navigate = useNavigate()

  const [typeFilter, setTypeFilter] = useState('all')
  const [locFilter, setLocFilter] = useState('all')
  const [compFilter, setCompFilter] = useState('all') // tracked-competitor id, or 'all'
  const [visibleCount, setVisibleCount] = useState(ALERT_PAGE)
  const [operatorType, setOperatorType] = useState<OperatorType>('both')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('unreviewed')
  const [sortKey, setSortKey] = useState<SortKey>('impact')
  const [search, setSearch] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set())
  const [toast, setToast] = useState<{ msg: string; color: string } | null>(null)

  // ── Triage state (session-level) ──────────────────────────────────────────
  // Review persists to the DB (markReviewed). Snooze is UI-only for now: the
  // alerts table carries no snoozed_until column, so it holds the operator's
  // intent within the session and clears on reload.
  const [snoozed, setSnoozed] = useState<Set<string>>(new Set())
  // Assign is a REAL workflow: it files an annotation against the alert with an
  // assignee_id (POST /api/v1/annotations, entity_type='alert'), which surfaces
  // the task on the Action Queue. `assignments` mirrors what was just filed this
  // session (alertId → assignee label) so the row reflects the action until
  // reload; the durable record lives on the Action Queue.
  const [assignments, setAssignments] = useState<Map<string, string>>(new Map())
  // The alert whose Assign panel is currently open (null = closed).
  const [assignTarget, setAssignTarget] = useState<Alert | null>(null)

  // Dropdown open states
  const [typeDdOpen, setTypeDdOpen] = useState(false)
  const [locDdOpen, setLocDdOpen] = useState(false)
  const [compDdOpen, setCompDdOpen] = useState(false)
  const [statusDdOpen, setStatusDdOpen] = useState(false)
  const [sortDdOpen, setSortDdOpen] = useState(false)
  const filterBarRef = useRef<HTMLDivElement | null>(null)

  const [locations, setLocations] = useState<{ id: string; name: string }[]>([])
  const [locError, setLocError] = useState(false)
  // Tracked competitors at the currently-selected location (cascading filter).
  const [competitors, setCompetitors] = useState<{ id: string; name: string }[]>([])

  // Resolve the selected location name → its id (the dropdown stores the name,
  // but the alerts + competitors endpoints are keyed by id). Locations have
  // unique names within an org, so this is a stable lookup.
  const selectedLocId = locFilter === 'all'
    ? null
    : (locations.find(l => l.name === locFilter)?.id ?? null)

  // Location + competitor filtering happens SERVER-SIDE (pass the ids to the
  // API) so picking a smaller store or a single rival isn't filtered out of the
  // newest-50 client slice. type/status/operator/search/sort stay client-side.
  const { alerts, loading, error: alertsError, markReviewed, batchMarkReviewed, refetch: refetchAlerts } = useAlerts({
    locationId: selectedLocId ?? undefined,
    competitorId: compFilter !== 'all' ? compFilter : undefined,
    reviewed: statusFilter,
    type: operatorType === 'both' ? undefined : operatorType,
  })

  const loadLocations = useCallback(() => {
    setLocError(false)
    // GET /api/v1/locations returns { locations, total, limit, offset } at the
    // top level (no `data` wrapper) — same shape BrandCarrier reads.
    authFetch(`${API}/api/v1/locations`)
      .then(r => r.json())
      .then(d => setLocations(d.locations || []))
      .catch(() => setLocError(true))
  }, [authFetch])

  useEffect(() => { loadLocations() }, [loadLocations])

  // Cascade: when a specific location is selected, load its tracked rivals into
  // the Competitor dropdown. Reset the competitor selection on every location
  // change so a stale rival from another location can't stay applied.
  useEffect(() => {
    setCompFilter('all')
    if (!selectedLocId) return
    let cancelled = false
    authFetch(`${API}/api/v1/locations/${selectedLocId}/competitors`)
      .then(r => r.json())
      .then(d => {
        if (cancelled) return
        // Endpoint returns { competitors, total } at the top level; each row
        // carries competitor_id + name.
        const rows: { competitor_id: string; name: string }[] = d.competitors || []
        // The route returns one row per tracked_competitors row, so the same
        // rival can repeat — either dup tracked rows (same competitor_id) or
        // true duplicate competitor rows that share a name under different ids.
        // Collapse to one dropdown entry per rival: by competitor_id first, then
        // by normalized name (trim + collapse whitespace + lowercase). The first
        // occurrence's id is kept; if name-merged ids differ, filtering by the
        // kept id is acceptable for now (display dedupe only).
        const seenId = new Set<string>()
        const seenName = new Set<string>()
        const deduped: { id: string; name: string }[] = []
        for (const c of rows) {
          const norm = c.name.trim().replace(/\s+/g, ' ').toLowerCase()
          if (seenId.has(c.competitor_id) || seenName.has(norm)) continue
          seenId.add(c.competitor_id)
          seenName.add(norm)
          deduped.push({ id: c.competitor_id, name: c.name })
        }
        setCompetitors(deduped)
      })
      .catch(() => { if (!cancelled) setCompetitors([]) })
    return () => { cancelled = true }
  }, [selectedLocId, authFetch])

  // With no location chosen there is no location-scoped roster to fetch, so the
  // rival list comes from the alerts on screen. It is deliberately NOT derived
  // from the *filtered* set: once you picked a rival the list would collapse to
  // that one rival and there would be no way back. Refreshed only while the
  // filter is off, so it always holds the full roster.
  useEffect(() => {
    if (selectedLocId || compFilter !== 'all') return
    const seen = new Map<string, string>()
    for (const a of alerts) {
      if (!a.competitor_id) continue
      if (!seen.has(a.competitor_id)) seen.set(a.competitor_id, a.competitor_name)
    }
    setCompetitors(
      [...seen].map(([id, name]) => ({ id, name })).sort((x, y) => x.name.localeCompare(y.name)),
    )
  }, [alerts, selectedLocId, compFilter])


  const showToast = (msg: string, color: string) => {
    setToast({ msg, color })
    setTimeout(() => setToast(null), 2800)
  }

  const closeAllDds = () => {
    setTypeDdOpen(false)
    setLocDdOpen(false)
    setCompDdOpen(false)
    setStatusDdOpen(false)
    setSortDdOpen(false)
  }

  // Close the filter dropdowns on outside-click + Escape. Only listens while
  // one is open. mousedown fires before the toggle's onClick; dropdown items
  // and the other triggers live inside filterBarRef, so this never fires for
  // them and existing pick/toggle behavior is unchanged.
  useEffect(() => {
    if (!typeDdOpen && !locDdOpen && !compDdOpen && !statusDdOpen && !sortDdOpen) return
    const onMouseDown = (e: MouseEvent) => {
      if (!filterBarRef.current?.contains(e.target as Node)) closeAllDds()
    }
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeAllDds()
    }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [typeDdOpen, locDdOpen, compDdOpen, statusDdOpen, sortDdOpen])

  // ── Computed ──────────────────────────────────────────────────────────────
  const unreviewedCount = alerts.filter(a => !a.reviewed).length
  // Drives the triage-framed subtitle: how much of the queue actually demands a
  // response now vs. is just FYI. Excludes snoozed (they've left the queue).
  const criticalCount = alerts.filter(a => !a.reviewed && !snoozed.has(a.id) && getSev(a) === 'critical').length

  let filtered = alerts.filter(a => {
    // Snoozed alerts leave the active queue for this session — the whole point
    // of snooze is "not now", so they must not sit in the list you're triaging.
    if (snoozed.has(a.id)) return false
    if (search) {
      const q = search.toLowerCase()
      if (!a.competitor_name.toLowerCase().includes(q) && !a.alert_type.includes(q)) return false
    }
    if (typeFilter !== 'all') {
      const typeMap: Record<string, string[]> = {
        price_drop: ['price_drop'],
        promo: ['new_promo'],
        sku: ['new_sku', 'sku_removed'],
      }
      const allowed = typeMap[typeFilter] || [typeFilter]
      if (!allowed.includes(a.alert_type)) return false
    }
    // location + competitor are filtered SERVER-SIDE (see the useAlerts call) so
    // a smaller store / single rival isn't lost to the newest-50 client slice.
    return true
  })

  // Sort
  filtered = [...filtered].sort((a, b) => {
    if (sortKey === 'impact') {
      const reviewedDelta = Number(a.reviewed) - Number(b.reviewed)
      return reviewedDelta || attentionScore(b) - attentionScore(a) || new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    }
    if (sortKey === 'newest') return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    if (sortKey === 'severity') {
      const sevOrder = { critical: 0, high: 1, medium: 2, info: 3 }
      return sevOrder[getSev(a)] - sevOrder[getSev(b)]
    }
    if (sortKey === 'rival') return a.competitor_name.localeCompare(b.competitor_name)
    if (sortKey === 'location') return (a.location_name || '').localeCompare(b.location_name || '')
    return 0
  })

  // Batch the queue rather than pouring it out. `shown` is what renders;
  // `filtered` stays the source of truth for counts, bulk actions and
  // "mark all reviewed", so capping the view never silently narrows an action.
  const shown = filtered.slice(0, visibleCount)
  const hiddenCount = filtered.length - shown.length
  const latestAlert = alerts.reduce<Alert | null>(
    (latest, alert) => !latest || new Date(alert.created_at) > new Date(latest.created_at) ? alert : latest,
    null,
  )
  const signalFeedIsStale = latestAlert
    ? Date.now() - new Date(latestAlert.created_at).getTime() > 48 * 3_600_000
    : false
  const alertGroups = sortKey === 'impact'
    ? [{ key: 'priority', label: 'Priority order', items: shown }]
    : (['today', 'yesterday', 'older'] as TimeBucket[]).map(bucket => ({
        key: bucket,
        label: BUCKET_LABEL[bucket],
        items: shown.filter(alert => timeBucket(alert.created_at) === bucket),
      }))

  // Changing any filter restarts the batch. Without this a narrowed result set
  // inherits an expanded count, the "show more" control vanishes, and the page
  // looks like it silently changed its mind about paging.
  useEffect(() => {
    setVisibleCount(ALERT_PAGE)
  }, [typeFilter, locFilter, compFilter, statusFilter, operatorType, search])

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleReview = async (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation()
    try {
      await markReviewed(id)
      if (expandedId === id) setExpandedId(null)
      showToast('Alert marked as reviewed', 'var(--accent)')
    } catch {
      showToast('Could not mark alert reviewed — please try again', 'var(--accent-alert)')
    }
  }

  // Snooze pulls an alert out of the active queue for this session — a way to
  // say "not now" without marking it handled. Session-only until the backend
  // gains a snoozed_until column.
  const handleSnooze = (id: string, e?: React.MouseEvent) => {
    e?.stopPropagation()
    setSnoozed(prev => new Set(prev).add(id))
    if (expandedId === id) setExpandedId(null)
    showToast('Snoozed — hidden from this queue for now', 'var(--warm)')
  }

  // Assign: open the assignment panel for this alert. Assigning is not a
  // self-claim toggle — it routes into the real assign flow where you pick who
  // owns the follow-up (teammate, not just yourself).
  const openAssign = (alert: Alert, e?: React.MouseEvent) => {
    e?.stopPropagation()
    setAssignTarget(alert)
  }

  // File the assignment. This is the genuine workflow: an annotation targeting
  // the alert (entity_type='alert') with the teammate's Clerk id and a notify
  // choice. The server validates the assignee (active member + location access)
  // and delivers the notification best-effort — a delivery failure never blocks
  // the task. Returns so the panel can show its own error.
  const submitAssign = async (alert: Alert, opts: { assigneeUserId: string; assigneeName: string; notify: NotifyOption; note: string }) => {
    const body = opts.note.trim() || `Follow up on ${getTitle(alert)}`
    const res = await authFetch(`${API}/api/v1/annotations`, {
      method: 'POST',
      body: JSON.stringify({
        body,
        entity_type: 'alert',
        entity_id: alert.id,
        assignee_id: opts.assigneeUserId,
        notify: opts.notify,
      }),
    })
    const d = await res.json().catch(() => ({}))
    if (!res.ok) throw new Error(d.error || `HTTP ${res.status}`)
    const deliveries: DeliveryOutcome[] = d?.data?.deliveries ?? []
    setAssignments(prev => {
      const next = new Map(prev)
      next.set(alert.id, opts.assigneeName)
      return next
    })
    setAssignTarget(null)
    const summary = summarizeDeliveries(deliveries)
    showToast(`Assigned to ${opts.assigneeName} — added to Today${summary ? ` · ${summary}` : ''}`, 'var(--accent)')
  }

  const handleMarkAll = async () => {
    const pending = filtered.filter(a => !a.reviewed)
    const { successIds, failedIds } = await batchMarkReviewed(pending.map(a => a.id))
    if (failedIds.length > 0) {
      showToast(`${successIds.length} reviewed, ${failedIds.length} failed — please retry`, 'var(--accent-alert)')
    } else {
      showToast(`${successIds.length} alerts marked reviewed`, 'var(--accent)')
    }
  }

  const toggleBulk = (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setBulkSelected(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
    if (expandedId) setExpandedId(null)
  }

  const bulkMarkReviewed = async () => {
    const ids = Array.from(bulkSelected)
    const { successIds, failedIds } = await batchMarkReviewed(ids)
    // Preserve failed IDs in the selection so the user can retry them.
    setBulkSelected(new Set(failedIds))
    if (failedIds.length > 0) {
      showToast(`${successIds.length} reviewed, ${failedIds.length} failed — please retry`, 'var(--accent-alert)')
    } else {
      showToast(`${ids.length} alert${ids.length === 1 ? '' : 's'} marked reviewed`, 'var(--accent)')
    }
  }

  const typeLabel = typeFilter === 'all' ? 'All types'
    : typeFilter === 'price_drop' ? 'Price drop'
    : typeFilter === 'promo' ? 'New promo'
    : typeFilter === 'sku' ? 'Catalog change'
    : typeFilter

  const locLabel = locFilter === 'all' ? 'All locations' : locFilter
  const compLabel = compFilter === 'all'
    ? 'All competitors'
    : (competitors.find(c => c.id === compFilter)?.name ?? 'All competitors')
  const statusLabel = statusFilter === 'unreviewed' ? 'Unreviewed' : statusFilter === 'reviewed' ? 'Reviewed' : 'All alerts'
  const sortLabel = sortKey === 'impact' ? 'Impact first' : sortKey === 'newest' ? 'Newest first' : sortKey === 'severity' ? 'Severity' : sortKey === 'rival' ? 'Rival name' : 'Location'


  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}
      onClick={closeAllDds}
    >
      {/* ── Page header (UI-002) ─────────────────────────────────────────
          This screen used to open with THREE competing bands: a topbar, a
          filter bar, and a greeting row that repeated the unreviewed count the
          topbar subtitle already carried. Live review (2026-08-17) reported
          the controls dominating before any alert was visible, and at narrow
          widths the three bands cost ~150px before the first row.

          The shared PageHeader fixes the hierarchy rather than the markup:
          band 1 is identity + the two primary actions, band 2 is every filter,
          visually subordinate. The greeting row is gone; its search box moved
          into band 2 where the rest of the query controls live. */}
      <PageHeader
        paddingX={28}
        titleTestId="alerts-title"
        title="Alert Feed"
        subtitle={
          <>
            {locFilter === 'all' ? 'All locations' : locFilter}
            {' · '}
            {loading
              ? 'Loading…'
              : unreviewedCount === 0
                ? 'Queue clear'
                : (
                  <>
                    {unreviewedCount} to review
                    {criticalCount > 0 && (
                      <span style={{ color: 'var(--danger)', fontWeight: 700 }}>
                        {' · '}{criticalCount} need a response now
                      </span>
                    )}
                  </>
                )}
          </>
        }
        onBack={() => navigate('/today')}
        actions={
          <>
            <button
              data-testid="alerts-mark-all-reviewed"
              onClick={handleMarkAll}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '8px 14px', borderRadius: 10,
                border: '1px solid var(--border-2)', background: 'var(--surface-2)',
                color: 'var(--text-2)', fontSize: 12, fontWeight: 600,
                cursor: 'pointer', fontFamily: 'var(--sans)',
                transition: 'var(--transition)',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--accent)'; (e.currentTarget as HTMLElement).style.color = 'var(--accent)' }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-2)'; (e.currentTarget as HTMLElement).style.color = 'var(--text-2)' }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13"><polyline points="20 6 9 17 4 12"/></svg>
              Mark all reviewed
            </button>
          </>
        }
        secondary={
          // display:contents so the ref/stopPropagation wrapper does not become
          // a flex item — the filters stay direct children of the wrapping band,
          // and the existing click-outside guard on filterBarRef still works.
          <div ref={filterBarRef} onClick={e => e.stopPropagation()} style={{ display: 'contents' }}>
          {/* Type */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase' as const, color: 'var(--text-3)' }}>Type</span>
            <div style={{ position: 'relative' }}>
              <button onClick={() => { setTypeDdOpen(o => !o); setLocDdOpen(false); setStatusDdOpen(false); setSortDdOpen(false) }}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8, background: typeFilter !== 'all' ? 'var(--accent-soft)' : 'var(--surface)', border: `1px solid ${typeFilter !== 'all' ? 'var(--accent)' : 'var(--border-2)'}`, color: typeFilter !== 'all' ? 'var(--accent)' : 'var(--text-2)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--sans)', whiteSpace: 'nowrap' as const }}>
                {typeLabel}<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="8" height="8"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              {typeDdOpen && <FilterPop options={[
                { value: 'all', label: 'All types' },
                { value: 'price_drop', label: 'Price drop', dot: 'var(--danger)' },
                { value: 'promo', label: 'New promo', dot: 'var(--warm)' },
                { value: 'sku', label: 'Catalog change', dot: 'var(--slate)' },
              ]} current={typeFilter} onSelect={v => { setTypeFilter(v); setTypeDdOpen(false) }} />}
            </div>
          </div>
  
          <div style={{ width: 1, height: 20, background: 'var(--border-2)' }} />
  
          {/* Location */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase' as const, color: 'var(--text-3)' }}>Location</span>
            <div style={{ position: 'relative' }}>
              <button onClick={() => { setLocDdOpen(o => !o); setTypeDdOpen(false); setStatusDdOpen(false); setSortDdOpen(false) }}
                title={locLabel}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8, background: locFilter !== 'all' ? 'var(--accent-soft)' : 'var(--surface)', border: `1px solid ${locFilter !== 'all' ? 'var(--accent)' : 'var(--border-2)'}`, color: locFilter !== 'all' ? 'var(--accent)' : 'var(--text-2)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--sans)', whiteSpace: 'nowrap' as const, minWidth: 0, maxWidth: '100%' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 150, display: 'block' }}>{locLabel}</span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="8" height="8"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              {locDdOpen && <FilterPop options={[
                { value: 'all', label: 'All locations' },
                ...locations.map(l => ({ value: l.name, label: l.name })),
              ]} current={locFilter} onSelect={v => { setLocFilter(v); setLocDdOpen(false) }} />}
            </div>
            {locError && (
              <button
                onClick={loadLocations}
                title="Couldn't load your locations — the filter list is unavailable. Click to retry."
                style={{
                  display: 'flex', alignItems: 'center', gap: 5, padding: '5px 9px',
                  borderRadius: 8, background: 'rgba(196,59,78,0.12)',
                  border: '1px solid rgba(196,59,78,0.3)', color: 'var(--danger)',
                  fontSize: 10.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--sans)',
                  whiteSpace: 'nowrap' as const,
                }}
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="11" height="11"><polyline points="1 4 1 10 7 10" /><path d="M3.51 15a9 9 0 1 0 .49-3.51" /></svg>
                Location filter unavailable — retry
              </button>
            )}
          </div>
  
          <div style={{ width: 1, height: 20, background: 'var(--border-2)' }} />
  
          {/* Competitor (cascades off Location — disabled until a specific location is picked) */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase' as const, color: 'var(--text-3)' }}>Competitor</span>
            <div style={{ position: 'relative' }}>
              <button
                disabled={competitors.length === 0}
                onClick={() => { if (!competitors.length) return; setCompDdOpen(o => !o); setTypeDdOpen(false); setLocDdOpen(false); setStatusDdOpen(false); setSortDdOpen(false) }}
                title={competitors.length === 0 ? (loading ? 'Loading rivals…' : 'No rivals in the current alerts') : compLabel}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8, background: compFilter !== 'all' ? 'var(--accent-soft)' : 'var(--surface)', border: `1px solid ${compFilter !== 'all' ? 'var(--accent)' : 'var(--border-2)'}`, color: competitors.length === 0 ? 'var(--text-3)' : compFilter !== 'all' ? 'var(--accent)' : 'var(--text-2)', fontSize: 11.5, fontWeight: 600, cursor: competitors.length ? 'pointer' : 'not-allowed', opacity: competitors.length ? 1 : 0.55, fontFamily: 'var(--sans)', whiteSpace: 'nowrap' as const, minWidth: 0, maxWidth: '100%' }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 150, display: 'block' }}>{compLabel}</span><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="8" height="8"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              {compDdOpen && competitors.length > 0 && <FilterPop options={[
                { value: 'all', label: 'All competitors' },
                ...competitors.map(c => ({ value: c.id, label: c.name })),
              ]} current={compFilter} onSelect={v => { setCompFilter(v); setCompDdOpen(false) }} />}
            </div>
          </div>
  
          <div style={{ width: 1, height: 20, background: 'var(--border-2)' }} />
  
          {/* Status */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase' as const, color: 'var(--text-3)' }}>Status</span>
            <div style={{ position: 'relative' }}>
              <button onClick={() => { setStatusDdOpen(o => !o); setTypeDdOpen(false); setLocDdOpen(false); setSortDdOpen(false) }}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8, background: statusFilter === 'unreviewed' ? 'rgba(196,59,78,0.12)' : 'var(--surface)', border: `1px solid ${statusFilter === 'unreviewed' ? 'rgba(196,59,78,0.3)' : 'var(--border-2)'}`, color: statusFilter === 'unreviewed' ? 'var(--danger)' : 'var(--text-2)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--sans)', whiteSpace: 'nowrap' as const }}>
                {statusLabel}<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="8" height="8"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              {statusDdOpen && <FilterPop options={[
                { value: 'all', label: 'All alerts' },
                { value: 'unreviewed', label: 'Unreviewed', dot: 'var(--danger)' },
                { value: 'reviewed', label: 'Reviewed', dot: 'var(--text-3)' },
              ]} current={statusFilter} onSelect={v => { setStatusFilter(v as StatusFilter); setStatusDdOpen(false) }} />}
            </div>
          </div>
  
          <div style={{ width: 1, height: 20, background: 'var(--border-2)' }} />
  
          {/* Sort */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <span style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 600, letterSpacing: '0.14em', textTransform: 'uppercase' as const, color: 'var(--text-3)' }}>Sort</span>
            <div style={{ position: 'relative' }}>
              <button onClick={() => { setSortDdOpen(o => !o); setTypeDdOpen(false); setLocDdOpen(false); setStatusDdOpen(false) }}
                style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 10px', borderRadius: 8, background: sortKey !== 'impact' ? 'var(--accent-soft)' : 'var(--surface)', border: `1px solid ${sortKey !== 'impact' ? 'var(--accent)' : 'var(--border-2)'}`, color: sortKey !== 'impact' ? 'var(--accent)' : 'var(--text-2)', fontSize: 11.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--sans)', whiteSpace: 'nowrap' as const }}>
                {sortLabel}<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="8" height="8"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              {sortDdOpen && <FilterPop options={[
                { value: 'impact', label: 'Impact first' },
                { value: 'newest', label: 'Newest first' },
                { value: 'severity', label: 'Severity (critical first)' },
                { value: 'rival', label: 'Rival name' },
                { value: 'location', label: 'Location' },
              ]} current={sortKey} onSelect={v => { setSortKey(v as SortKey); setSortDdOpen(false) }} />}
            </div>
          </div>
  
          {/* Operator type filter */}
          <OperatorTypeFilter value={operatorType} onChange={setOperatorType} />
  
            {/* Right cluster — result count and search. Search moved here from
                the deleted greeting row; it is a query control and belongs with
                the other query controls. */}
            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 10 }}>
              {filtered.length > 0 && (
                <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', whiteSpace: 'nowrap' }}>
                  {filtered.length} alert{filtered.length === 1 ? '' : 's'}
                </span>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border-2)', padding: '4px 10px', width: 200 }}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="12" height="12" style={{ color: 'var(--text-3)', flexShrink: 0 }}><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.5-4.5"/></svg>
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder="Search rivals, SKUs…"
                  style={{ flex: 1, minWidth: 0, background: 'transparent', border: 'none', outline: 'none', fontSize: 12, color: 'var(--text-1)', fontFamily: 'var(--sans)' }}
                />
                <span style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)', background: 'var(--surface-3)', padding: '1px 5px', borderRadius: 4, border: '1px solid var(--border)', flexShrink: 0 }}>⌘K</span>
              </div>
            </div>
          </div>
        }
      />

      {!loading && !alertsError && signalFeedIsStale && (
        <div className="alert-feed-trust" role="status">
          <span>No new signals in 48 hours</span>
          <span>Latest signal {latestAlert ? timeAgo(latestAlert.created_at) : 'unavailable'} · verify collection health before acting</span>
        </div>
      )}

      {/* ── Body ──────────────────────────────────────────────────────────────
          The list used to run the full viewport width on the bare page `--bg`
          (a pale grey wash), so every row's title hugged the left and its
          metadata hugged the right — a dead band down the middle and a washed,
          floating feel. Instead the queue now sits on a single centred white
          surface with a capped, readable width: a defined panel, not a field. */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', padding: '14px 24px 0' }}>

        {/* Alert list — one centred surface panel; sticky time headers ride this
            scroll container (s12). */}
        <div data-testid="alerts-list" className="list-scroll" style={{ flex: 1, overflowY: 'auto', minHeight: 0, paddingBottom: 24, width: '100%', maxWidth: 960, margin: '0 auto', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 16, boxShadow: 'var(--card-shadow)' }}>
          {loading ? (
            <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--text-3)', fontSize: 12 }}>
              Pulling latest intelligence from {locations.length || 0} markets…
            </div>
          ) : alertsError ? (
            <div data-testid="alerts-error-state" style={{ padding: '48px 24px', textAlign: 'center' }}>
              <div style={{ color: 'var(--text-1)', fontSize: 15, fontWeight: 750, marginBottom: 6 }}>Alerts could not be loaded</div>
              <div style={{ color: 'var(--text-3)', fontSize: 12.5, lineHeight: 1.55, marginBottom: 16 }}>This queue is incomplete. Retry before making a pricing or promotion decision.</div>
              <button onClick={() => refetchAlerts()} style={{ border: '1px solid var(--border-2)', borderRadius: 9, padding: '8px 13px', background: 'var(--surface-2)', color: 'var(--text-1)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Retry</button>
            </div>
          ) : filtered.length === 0 ? (
            <EmptyStateCard
              statusFilter={statusFilter}
              hasFilters={typeFilter !== 'all' || locFilter !== 'all' || compFilter !== 'all'}
              locationCount={locations.length}
              onReset={() => { setTypeFilter('all'); setLocFilter('all'); setCompFilter('all'); setStatusFilter('unreviewed'); setSortKey('impact') }}
            />
          ) : (
            alertGroups.map(group => {
              const { items } = group
              if (items.length === 0) return null
              return (
                <div key={group.key}>
                  <TimeGroupHeader label={group.label} count={items.length} />
                  {items.map((alert, index) => (
                    <AlertRow
                      key={alert.id}
                      alert={alert}
                      triageNext={sortKey === 'impact' && index === 0 && statusFilter !== 'reviewed'}
                      expanded={expandedId === alert.id}
                      bulkSelected={bulkSelected.has(alert.id)}
                      assignedTo={assignments.get(alert.id) ?? null}
                      onExpand={() => setExpandedId(expandedId === alert.id ? null : alert.id)}
                      onBulkToggle={e => toggleBulk(alert.id, e)}
                      onReview={e => handleReview(alert.id, e)}
                      onSnooze={e => handleSnooze(alert.id, e)}
                      onAssign={e => openAssign(alert, e)}
                      onNavigatePrices={() => navigate('/prices')}
                      onOpenDeepDive={() => navigate(`/prices/change/${alert.id}`)}
                    />
                  ))}
                </div>
              )
            })
          )}

          {/* A triage queue ends. A feed just keeps going — and an unbounded
              feed teaches you to stop reading it, which is the opposite of what
              this page is for (founder review 2026-08-18). The batch is sized
              to a sitting, and the remainder is one click away rather than
              hidden. */}
          {!loading && hiddenCount > 0 && (
            <div style={{ padding: '14px 28px 8px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <button
                data-testid="alerts-show-more"
                onClick={() => setVisibleCount(n => n + ALERT_PAGE)}
                style={{
                  padding: '7px 14px', borderRadius: 'var(--r-sm)',
                  border: '1.5px solid var(--border-2)', background: 'var(--surface)',
                  color: 'var(--text-1)', fontSize: 12, fontWeight: 600,
                  fontFamily: 'var(--sans)', cursor: 'pointer',
                }}
              >
                Show {Math.min(ALERT_PAGE, hiddenCount)} more
              </button>
              <span style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)' }}>
                {shown.length} of {filtered.length} shown
              </span>
              <button
                onClick={() => setVisibleCount(filtered.length)}
                style={{
                  marginLeft: 'auto', background: 'none', border: 'none', padding: 0,
                  cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: 11,
                  fontWeight: 700, color: 'var(--accent)',
                }}
              >
                Show all {filtered.length}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* ── Bulk action bar ───────────────────────────────────────────────── */}
      {bulkSelected.size > 0 && (
        <div style={{
          position: 'fixed', bottom: 0, left: 64, right: 0,
          padding: '14px 28px',
          background: 'var(--surface-solid)',
          borderTop: '1px solid var(--border-2)',
          backdropFilter: 'blur(20px)',
          boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
          zIndex: 100,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{
              background: 'var(--accent)', color: 'var(--cta-fg-on-pos)',
              borderRadius: 20, padding: '2px 10px',
              fontSize: 12, fontWeight: 700, fontFamily: 'var(--mono)',
            }}>{bulkSelected.size}</span>
            <span style={{ fontSize: 12.5, color: 'var(--text-2)', fontWeight: 500 }}>
              alert{bulkSelected.size === 1 ? '' : 's'} selected
            </span>
          </div>
          <button
            data-testid="alerts-bulk-mark-reviewed"
            onClick={bulkMarkReviewed}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', borderRadius: 20, border: 'none', background: 'var(--accent)', color: 'var(--cta-fg-on-pos)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--sans)', boxShadow: 'var(--cta-shadow)' }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13"><polyline points="20 6 9 17 4 12"/></svg>
            Mark all reviewed
          </button>
          <button
            onClick={() => setBulkSelected(new Set())}
            style={{ padding: '8px 14px', borderRadius: 20, border: '1px solid var(--border-2)', background: 'transparent', color: 'var(--text-2)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--sans)' }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* ── Toast ──────────────────────────────────────────────────────── */}
      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          zIndex: 9999,
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'var(--surface-solid)', border: '1px solid var(--border-2)',
          borderRadius: 20, padding: '10px 18px',
          boxShadow: 'var(--card-shadow-lg)',
          fontSize: 12.5, fontWeight: 600, color: 'var(--text-1)',
        }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: toast.color, flexShrink: 0 }} />
          {toast.msg}
        </div>
      )}

      {/* ── Assign panel ───────────────────────────────────────────────────
          The real assignment flow: name the owner + a follow-up note, filed as
          a task the Action Queue reads back. Not a self-claim. */}
      {assignTarget && (
        <AlertAssignModal
          alert={assignTarget}
          title={getTitle(assignTarget)}
          onClose={() => setAssignTarget(null)}
          onSubmit={submitAssign}
          onOpenQueue={() => navigate('/today')}
        />
      )}
    </div>
  )
}

// ── Sub-components ─────────────────────────────────────────────────────────

// Assign panel — the real assignment flow. Names the owner of the follow-up and
// files it as a task (annotation w/ assignee_id) that lands on the Action Queue.
// Deliberately supports assigning to anyone, not just the current operator.
// A single per-row triage control (Snooze / Assign / Review). Stops click
// propagation so acting on a row never also expands it.
function RowActionBtn({ title, active, onClick, children }: {
  title: string
  active?: boolean
  onClick: (e: React.MouseEvent) => void
  children: React.ReactNode
}) {
  const [hov, setHov] = useState(false)
  return (
    <button
      title={title}
      aria-label={title}
      onClick={(e) => { e.stopPropagation(); onClick(e) }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 26, height: 26, borderRadius: 7, cursor: 'pointer',
        border: `1px solid ${active ? 'var(--accent)' : hov ? 'var(--border-hot)' : 'transparent'}`,
        background: active ? 'var(--accent-soft)' : hov ? 'var(--surface-2)' : 'transparent',
        color: active ? 'var(--accent)' : hov ? 'var(--text-1)' : 'var(--text-3)',
        transition: 'all 0.12s',
      }}
    >
      {children}
    </button>
  )
}

function FilterPop({ options, current, onSelect }: {
  options: { value: string; label: string; dot?: string }[]
  current: string
  onSelect: (v: string) => void
}) {
  return (
    <div style={{
      position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 300,
      background: 'var(--surface)', border: '1px solid var(--border-2)',
      borderRadius: 10, boxShadow: 'var(--card-shadow-lg)', minWidth: 170, padding: 4,
    }}
      onClick={e => e.stopPropagation()}
    >
      {options.map(opt => (
        <button
          key={opt.value}
          onClick={() => onSelect(opt.value)}
          style={{
            display: 'flex', width: '100%', alignItems: 'center', gap: 7,
            padding: '7px 10px', borderRadius: 7, border: 'none', cursor: 'pointer',
            background: current === opt.value ? 'var(--accent-soft)' : 'transparent',
            color: current === opt.value ? 'var(--accent)' : 'var(--text-1)',
            fontSize: 12, fontFamily: 'var(--sans)', textAlign: 'left' as const,
          }}
        >
          {opt.dot && <span style={{ width: 7, height: 7, borderRadius: '50%', background: opt.dot, flexShrink: 0 }} />}
          {opt.label}
        </button>
      ))}
    </div>
  )
}

// Sticky time-group header (s12 `.time-group-header`).
function TimeGroupHeader({ label, count }: { label: string; count: number }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '11px 24px 7px', position: 'sticky', top: 0, zIndex: 10,
      background: 'linear-gradient(180deg,var(--surface) 80%,transparent)',
      backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
    }}>
      <span style={{ fontFamily: 'var(--mono)', fontSize: 9, fontWeight: 700, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--text-3)' }}>{label}</span>
      <span style={{ flex: 1, height: 1, background: 'var(--border)' }} />
      <span className="tnum" style={{ fontFamily: 'var(--mono)', fontSize: 9, color: 'var(--text-3)' }}>{count}</span>
    </div>
  )
}

const SEV_PILL_TONE: Record<Sev, PillTone> = { critical: 'danger', high: 'warning', medium: 'accent', info: 'neutral' }

function AlertRow({ alert, triageNext, expanded, bulkSelected, assignedTo, onExpand, onBulkToggle, onReview, onSnooze, onAssign, onNavigatePrices, onOpenDeepDive }: {
  alert: Alert
  triageNext: boolean
  expanded: boolean
  bulkSelected: boolean
  assignedTo: string | null
  onExpand: () => void
  onBulkToggle: (e: React.MouseEvent) => void
  onReview: (e: React.MouseEvent) => void
  onSnooze: (e: React.MouseEvent) => void
  onAssign: (e: React.MouseEvent) => void
  onNavigatePrices: () => void
  onOpenDeepDive: () => void
}) {
  const [hovered, setHovered] = useState(false)
  const assigned = !!assignedTo
  const sev = getSev(alert)
  const chips = getChips(alert)
  if (triageNext) chips.unshift({ label: 'TRIAGE NEXT', tone: 'danger' })
  if (assignedTo) chips.push({ label: `ASSIGNED · ${assignedTo.toUpperCase()}`, tone: 'accent' })
  const hasPrice = !!(alert.old_value && alert.new_value)
  const isPriceMove = alert.alert_type === 'price_drop' || alert.alert_type === 'price_increase'
  const pct = hasPrice
    ? Math.round((parseFloat(alert.old_value!) - parseFloat(alert.new_value!)) / parseFloat(alert.old_value!) * 100)
    : 0
  // Muted recommendation when the alert is informational or has no concrete price gap.
  const actionable = sev === 'critical' || sev === 'high'

  // Mono eyebrow: RIVAL · LOCATION (mirrors the mockup `.det-rival-line`).
  const eyebrow = [alert.competitor_name.toUpperCase(), alert.location_name?.toUpperCase()]
    .filter(Boolean).join(' · ')

  // Supporting facts as a calm inline line rather than four boxed KPI tiles —
  // they inform the decision, they are not the headline.
  const facts: { text: string; strong?: boolean }[] = []
  if (hasPrice) {
    facts.push({ text: `−$${(parseFloat(alert.old_value!) - parseFloat(alert.new_value!)).toFixed(0)} rival change`, strong: true })
    facts.push({ text: `−${pct}%`, strong: true })
  }
  facts.push({ text: timeAgo(alert.created_at) })
  facts.push({ text: alert.confidence === 'high' ? 'High confidence' : 'Medium confidence' })

  const recText = decisionImpact(alert)

  const expansion = (
    <DetailPanel eyebrow={eyebrow} title={getTitle(alert)}>
      {/* Supporting facts — one quiet mono line, not a KPI slab. */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '2px 9px', fontFamily: 'var(--mono)', fontSize: 11, marginBottom: 12 }}>
        {facts.map((f, i) => (
          <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}>
            {i > 0 && <span style={{ color: 'var(--border-hot)' }}>·</span>}
            <span style={{ color: f.strong ? 'var(--danger)' : 'var(--text-2)', fontWeight: f.strong ? 700 : 500 }}>{f.text}</span>
          </span>
        ))}
      </div>

      {/* Recommendation — editorial thin rule, no pale fill. */}
      <div style={{
        borderLeft: `2px solid ${actionable ? 'var(--accent)' : 'var(--border-hot)'}`,
        paddingLeft: 12, marginBottom: 16,
        fontFamily: 'var(--sans)', fontSize: 12.5, lineHeight: 1.5,
        color: actionable ? 'var(--text-1)' : 'var(--text-2)',
      }}>
        {recText}
      </div>

      {/* Actions — three tiers so it reads at a glance:
          primary (filled) · secondary (ghost) · navigation (text links, pushed right). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' as const }}>
        <button
          onClick={onReview}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', borderRadius: 9, border: 'none', background: 'var(--accent)', color: 'var(--cta-fg-on-pos)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--sans)', boxShadow: 'var(--cta-shadow)' }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="13" height="13"><polyline points="20 6 9 17 4 12"/></svg>
          Mark reviewed
        </button>
        <button
          onClick={onSnooze}
          style={{ padding: '8px 12px', borderRadius: 9, border: '1px solid var(--border-2)', background: 'transparent', color: 'var(--text-2)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--sans)' }}
        >
          Snooze
        </button>
        <button
          onClick={onAssign}
          style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px', borderRadius: 9, border: `1px solid ${assigned ? 'var(--accent)' : 'var(--border-2)'}`, background: assigned ? 'var(--accent-soft)' : 'transparent', color: assigned ? 'var(--accent)' : 'var(--text-2)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--sans)' }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          {assigned ? `Assigned · ${assignedTo}` : 'Assign'}
        </button>

        {/* Navigation — quietest tier, plain text links pushed to the edge. */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 16 }}>
          {isPriceMove && (
            <button
              data-testid="feed-view-price-change-detail"
              onClick={(e) => { e.stopPropagation(); onOpenDeepDive() }}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: 11.5, fontWeight: 600, color: 'var(--accent)' }}
            >
              Price change detail →
            </button>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onNavigatePrices() }}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'var(--sans)', fontSize: 11.5, fontWeight: 600, color: 'var(--text-3)' }}
          >
            Price matrix →
          </button>
        </div>
      </div>
    </DetailPanel>
  )

  return (
    <div
      data-testid={`alert-row-${alert.id}`}
      style={{ position: 'relative' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Hover/bulk-mode checkbox — seated at the row's content left edge (24px,
          matching FeedItem's padding) and vertically centred on the icon, so the
          checkbox, icon and title share one left rhythm instead of the box
          hanging out in the panel margin. */}
      <div
        onClick={onBulkToggle}
        style={{
          position: 'absolute', left: 24, top: 20, zIndex: 3,
          width: 16, height: 16, borderRadius: 4, flexShrink: 0,
          border: `1.5px solid ${bulkSelected ? 'var(--accent)' : 'var(--border-hot)'}`,
          background: bulkSelected ? 'var(--accent)' : 'var(--surface)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', transition: 'opacity 0.12s, background 0.12s, border-color 0.12s',
          opacity: hovered || bulkSelected ? 1 : 0,
        }}
      >
        {bulkSelected && <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3" width="10" height="10"><polyline points="20 6 9 17 4 12"/></svg>}
      </div>

      <FeedItem
        variant="row"
        tone={sevTone(sev, alert.alert_type)}
        icon={<AlertIcon type={alert.alert_type} />}
        title={getTitle(alert)}
        time={timeAgo(alert.created_at)}
        sub={getSub(alert)}
        chips={chips.length > 0 ? chips.map((chip, i) => (
          <Pill key={i} variant="chip" tone={chip.tone}>{chip.label}</Pill>
        )) : undefined}
        trailing={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {triageNext && (
              <button
                data-testid="alerts-triage-next"
                onClick={(event) => { event.stopPropagation(); onExpand() }}
                style={{ border: 'none', borderRadius: 8, padding: '6px 10px', background: 'var(--accent)', color: 'var(--cta-fg-on-pos)', fontFamily: 'var(--sans)', fontSize: 10.5, fontWeight: 700, cursor: 'pointer', boxShadow: 'var(--cta-shadow)', whiteSpace: 'nowrap' }}
              >
                Triage next →
              </button>
            )}
            {/* Per-row triage controls. Always present (brighten on hover) so the
                page reads as a queue you act on, not a feed you scroll. */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 2, opacity: hovered ? 1 : 0.5, transition: 'opacity 0.12s' }}>
              <RowActionBtn title="Snooze — hide for now" onClick={onSnooze}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg>
              </RowActionBtn>
              <RowActionBtn title={assigned ? `Assigned to ${assignedTo} — reassign` : 'Assign to a teammate'} active={assigned} onClick={onAssign}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="14" height="14"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </RowActionBtn>
              <RowActionBtn title="Mark reviewed" onClick={onReview}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="14" height="14"><polyline points="20 6 9 17 4 12"/></svg>
              </RowActionBtn>
            </div>
            <Pill
              variant="chip"
              tone={SEV_PILL_TONE[sev]}
              style={{ borderRadius: 100, padding: '2px 8px', fontSize: 8.5, letterSpacing: '0.08em' }}
            >
              {getSevLabel(sev)}
            </Pill>
          </div>
        }
        unread={!alert.reviewed}
        reviewed={alert.reviewed}
        selected={expanded}
        onClick={onExpand}
        expansion={expansion}
        // Expanded: neutralise FeedItem's pale-blue `accent-soft` wash to a calm
        // surface tone — the 3px left accent bar already marks the open row, so
        // the whole slab needn't be tinted blue.
        style={expanded ? { background: 'var(--surface-2)' } : bulkSelected ? { background: 'var(--accent-soft)' } : undefined}
      />
    </div>
  )
}

function EmptyStateCard({ statusFilter, hasFilters, locationCount, onReset }: {
  statusFilter: StatusFilter
  hasFilters: boolean
  locationCount: number
  onReset: () => void
}) {
  if (statusFilter !== 'unreviewed' || hasFilters) {
    return (
      <div style={{ textAlign: 'center', padding: '60px 24px' }}>
        <div style={{ width: 48, height: 48, borderRadius: '50%', background: 'var(--surface-2)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: 'var(--text-3)' }}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4.5-4.5"/></svg>
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text-1)', marginBottom: 8 }}>No alerts match</div>
        <div style={{ fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.6, marginBottom: 20 }}>Your filters returned no results. Try adjusting type, location, or status.</div>
        <button onClick={onReset} style={{ padding: '9px 18px', borderRadius: 20, border: 'none', background: 'var(--accent)', color: 'var(--cta-fg-on-pos)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--sans)', boxShadow: 'var(--cta-shadow)' }}>Clear all filters</button>
      </div>
    )
  }
  return (
    <div style={{ textAlign: 'center', padding: '60px 24px' }}>
      <div style={{ width: 52, height: 52, borderRadius: '50%', background: 'var(--accent-soft)', border: '1px solid rgba(11,184,184,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', color: 'var(--accent)' }}>
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" width="22" height="22"><polyline points="20 6 9 17 4 12"/></svg>
      </div>
      <div style={{ fontSize: 17, fontWeight: 800, color: 'var(--text-1)', marginBottom: 8, letterSpacing: '-0.02em' }}>You're all caught up</div>
      <div style={{ fontSize: 12.5, color: 'var(--text-3)', lineHeight: 1.6, maxWidth: 360, margin: '0 auto 6px' }}>
        Every alert in your feed has been reviewed. We'll notify you when new intelligence arrives.
      </div>
      <div style={{ fontFamily: 'var(--mono)', fontSize: 10, color: 'var(--text-3)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 24 }}>
        Monitoring tracked rivals
      </div>
    </div>
  )
}
